// Design tokens compartidos — el "Linear-style" del sistema LGR.
// Usalo para que toda pantalla respire el mismo aire: mono solo para
// identificadores, sans para texto humano, tabular-nums, paleta consistente.
//
// Reglas:
// - Mono SOLO para: nº de comprobante, código, fecha, importe, CUIT.
// - Sans para: nombres, etiquetas, mensajes humanos.
// - Color en estados (badges) y en el link primario. El resto es slate.

export const font = {
  sans: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
  mono: 'ui-monospace, "SF Mono", Menlo, Monaco, Consolas, monospace',
}

// Paleta — slate como neutro, azul como primario, violeta para T80/NE
export const colors = {
  // Texto
  text:        '#0f172a',  // titulares y datos primarios
  textStrong:  '#1e293b',  // titulares fuertes
  textSec:     '#64748b',  // labels, metadata
  textSub:     '#475569',  // texto secundario fuerte (chips, controles, avatares)
  textMeta:    '#94a3b8',  // texto muy secundario
  textPlh:    '#cbd5e1',  // placeholders y separadores

  // Bordes
  border:      '#e2e8f0',
  borderSoft:  '#f1f5f9',
  borderStrong:'#0f172a',  // ej: línea inferior del header del documento

  // Fondos
  bg:          '#ffffff',
  bgSoft:      '#f8fafc',
  overlay:     'rgba(15, 23, 42, 0.5)',  // fondo de modales
  codeBg:      '#0f172a',  // bloque de JSON / código crudo (slate-900)
  codeText:    '#e2e8f0',  // texto sobre codeBg

  // Link / acción — variables CSS (index.css): azul en LGR, VERDE dentro de
  // la empresa EMP 900 (`[data-empresa="900"]`). Por eso no son hex: no
  // concatenarles alpha (`${colors.link}33` no funciona con var()).
  link:        'var(--lgr-link)',       // #1d4ed8
  linkBg:      'var(--lgr-link-bg)',    // #eff6ff
  linkBorder:  'var(--lgr-link-border)', // #bfdbfe
  t80Border:   '#ddd6fe',  // borde del violeta (T80/NE, compra)
  infoBorder:  '#bae6fd',  // borde del celeste (info)
  warnBorder:  '#fde68a',  // borde del ámbar (warn)
  // Color de la ACCIÓN PRIMARIA (el de Boton.jsx variante primary): botones
  // sólidos "Guardar" / "+ Nueva" / CTA del móvil. No confundir con `link`.
  primary:     'var(--lgr-primary)',    // #1e40af

  // Badges T90/T80
  t90Bg:       '#eff6ff', t90Text: '#1d4ed8',
  t80Bg:       '#f5f3ff', t80Text: '#6d28d9',
  zonaBg:      '#f1f5f9', zonaText:'#475569',

  // Barra de progreso de entregas (salidas, distribución)
  progreso:    '#22c55e',

  // Caja de atención (formularios inline tipo "anular"/"abrir caso":
  // fondo naranja suave + borde orange-200). Antes cada página elegía su
  // ámbar y divergían (auditoría NE detalle, 31 ago 2026).
  attnBg:      '#fff7ed', attnBorder: '#fed7aa',

  // Estados semánticos (badges, banners)
  ok:          '#166534', okBg:    '#dcfce7', okSoft: '#f0fdf4',   // okSoft = fondo de fila/card "hecho"
  warn:        '#92400e', warnBg:  '#fef3c7', warnStrong: '#d97706', // warnStrong = punto/ícono de atención
  warnSoft:    '#fefce8',  // fondo de fila "con atención suave" (ej. renglón con descuento en NV)
  err:         '#991b1b', errBg:   '#fee2e2',
  errStrong:   '#dc2626', errBorder: '#fecaca', // errStrong = número/ícono en rojo vivo (falta stock, validación)
  info:        '#075985', infoBg:  '#e0f2fe',
  infoSoft:    '#f0f9ff',  // fondo de la ficha condensada del cliente (NV)
}

// Escala tipográfica
export const text = {
  display: 28,  // nº de remito grande, monto destacado
  title:   16,  // nombre cliente, h2 secciones
  body:    13,  // texto base (tabla, párrafos)
  sec:     12,  // metadata, labels
  meta:    11,  // headers de tabla uppercase, badges
}

export const space = {
  xs: 4, sm: 6, md: 8, lg: 12, xl: 16, '2xl': 20, '3xl': 24,
}

export const radius = {
  xs: 4, sm: 6, md: 8, lg: 10, pill: 999,
}

export const shadow = {
  // Popovers / menús desplegables (BotonMenu y futuros)
  dropdown: '0 4px 12px rgba(15, 23, 42, 0.08)',
  card: '0 1px 4px rgba(15, 23, 42, 0.07)',   // cards de listado (Retiros, Reseñas, Distribución)
}

// ─── Helpers reusables ──────────────────────────────────────────────

export const sx = {
  // Mono con tabular-nums (números alineados en columnas)
  mono: { fontFamily: font.mono, fontVariantNumeric: 'tabular-nums' },

  // Header de card / sección. 11px uppercase, slate.
  cardTitle: {
    margin: 0, fontSize: text.meta, fontWeight: 600,
    color: colors.textSec, textTransform: 'uppercase',
    letterSpacing: '0.05em',
  },

  // Card blanco con borde sutil
  card: {
    background: colors.bg, borderRadius: radius.lg,
    border: `1px solid ${colors.border}`, padding: space.xl,
  },

  // Tabla genérica
  // Chip gris no clickeable de "tarea hecha / esperando" (patrón de listados:
  // "⏳ Enviado" en Pagos, "⏳ Espera la aprobación" en Presupuestos)
  chipEstado: {
    display: 'inline-block', padding: '4px 12px', background: colors.bgSoft,
    border: `1px solid ${colors.border}`, borderRadius: radius.sm,
    fontSize: text.sec, fontWeight: 600, color: colors.textSec, whiteSpace: 'nowrap', cursor: 'default',
  },
  table: {
    width: '100%', borderCollapse: 'collapse', fontSize: text.body,
    fontVariantNumeric: 'tabular-nums',
  },
  th: {
    padding: `${space.md}px ${space.xl}px`, textAlign: 'left',
    background: colors.bgSoft, borderBottom: `1px solid ${colors.border}`,
    fontWeight: 600, fontSize: text.meta, color: colors.textSec,
    textTransform: 'uppercase', letterSpacing: '0.05em', whiteSpace: 'nowrap',
  },
  td: {
    padding: `0 ${space.xl}px`, verticalAlign: 'middle',
    fontSize: text.body, color: colors.text, lineHeight: 1.2,
  },

  // Badges chicos uppercase
  badge: {
    display: 'inline-block', padding: '2px 8px', borderRadius: radius.xs,
    fontSize: text.meta, fontWeight: 600, letterSpacing: '0.02em',
    textTransform: 'uppercase',
  },

  // Botones
  btnPrimary: {
    padding: '6px 12px', background: colors.text, color: colors.bg,
    border: 'none', borderRadius: radius.sm, fontSize: text.body,
    fontWeight: 500, cursor: 'pointer',
  },
  btnSecondary: {
    padding: '6px 12px', background: colors.bg, color: colors.text,
    border: `1px solid ${colors.textPlh}`, borderRadius: radius.sm,
    fontSize: text.body, fontWeight: 500, cursor: 'pointer',
  },
  btnGhost: {
    padding: '4px 10px', background: 'transparent',
    border: `1px solid ${colors.border}`, borderRadius: radius.sm,
    fontSize: text.sec, fontWeight: 500, cursor: 'pointer',
    color: '#475569',
  },
  btnDestructive: {
    padding: '6px 12px', background: colors.bg, color: colors.err,
    border: '1px solid #fecaca', borderRadius: radius.sm,
    fontSize: text.body, fontWeight: 500, cursor: 'pointer',
  },

  // Toolbar sticky (header de pantalla con acciones)
  toolbar: {
    position: 'sticky', top: 0, zIndex: 10,
    display: 'flex', alignItems: 'center', gap: space.lg,
    padding: `${space.lg}px 0`, marginBottom: space.xl,
    background: colors.bg, borderBottom: `1px solid ${colors.border}`,
    boxShadow: `0 1px 0 ${colors.border}`,
  },

  // Filtro panel
  filterBar: {
    display: 'flex', gap: space.md, flexWrap: 'wrap', alignItems: 'center',
    marginBottom: space.md, background: colors.bg, padding: space.lg,
    borderRadius: radius.md, border: `1px solid ${colors.border}`,
  },
  input: {
    padding: `${space.sm}px ${space.lg}px`,
    border: `1px solid ${colors.textPlh}`, borderRadius: radius.sm,
    fontSize: text.body, fontFamily: 'inherit', minWidth: 130,
  },

  // Pill (preset / chip de filtro)
  pill: {
    padding: '5px 12px', borderRadius: radius.pill,
    border: `1px solid ${colors.border}`, background: colors.bgSoft,
    fontSize: '0.78rem', fontWeight: 600, cursor: 'pointer',
    color: '#475569', outline: 'none', transition: 'all 0.1s',
  },
  pillActive: {
    background: colors.linkBg, color: colors.link,
    border: `1px solid ${colors.link}`,
  },

  // Placeholder de celda vacía
  placeholder: { color: colors.textPlh },

  // Bloque de JSON / respuesta cruda (fondo oscuro, mono)
  jsonBox: {
    background: colors.codeBg, color: colors.codeText,
    padding: space.lg, borderRadius: radius.sm,
    fontSize: text.sec, fontFamily: font.mono,
    maxHeight: 320, overflow: 'auto', whiteSpace: 'pre', lineHeight: 1.45,
  },
}
